import { BrandLinkElement } from "app/common/components/structured-text/BrandLinkElement"
import { ImageElement } from "app/common/components/structured-text/ImageElement"
import { InstagramElement } from "app/common/components/structured-text/InstagramElement"
import { BlockTextType, CustomElement, CustomText } from "app/common/constants/structuredText"
import { idEquals } from "app/common/core/types"
import { StructuredTextEntities } from "app/common/utilities/structuredText"
import { array, option } from "fp-ts"
import { pipe } from "fp-ts/function"
import React, { FC, Fragment, ReactNode } from "react"
import { Key } from "react"
import { tw } from "twind"

export const isElement = (value: unknown): value is CustomElement =>
  (value as CustomElement).type !== undefined

const isLeaf = (value: unknown): value is CustomText =>
  (value as CustomText).text !== null && (value as CustomText).text !== undefined

const renderElement = (element: CustomElement, key: Key, entities: StructuredTextEntities) => {
  switch (element.type) {
    case BlockTextType.BlockQuote:
      return (
        <blockquote className={tw(`pl-4 my-2`, `text-gray-400`, `border-l-2`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </blockquote>
      )

    case BlockTextType.UnorderedList:
      return (
        <ul className={tw(`list-disc pl-6`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </ul>
      )

    case BlockTextType.Heading1:
      return (
        <h1 className={tw(`text-3xl`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </h1>
      )

    case BlockTextType.Heading2:
      return (
        <h2 className={tw(`text-2xl`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </h2>
      )

    case BlockTextType.Heading3:
      return (
        <h3 className={tw(`text-xl`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </h3>
      )

    case BlockTextType.ListItem:
      return (
        <li key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </li>
      )

    case BlockTextType.OrderedList:
      return (
        <ol className={tw(`list-decimal pl-6`)} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </ol>
      )

    case "external-link":
      return (
        <a href={element.url} key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </a>
      )

    case "image":
      const props = { attributes: {}, children: [], element }

      return <ImageElement key={key} {...props} />

    case "instagram": {
      const props = { attributes: {}, children: [], element }

      return <InstagramElement {...props} key={key} />
    }

    case "tweet":
      return <div dangerouslySetInnerHTML={{ __html: element.markup }} key={key} />

    // case "article-link": {
    //   const props = { attributes: {}, children: [], element }

    //   return pipe(
    //     entities.articles,
    //     array.findFirst(idEquals(element.id)),
    //     option.fold(
    //       () => <></>,
    //       (article) => <ArticleLinkElement key={key} {...props} article={article} />
    //     )
    //   )
    // }

    case "brand-link": {
      const props = { attributes: {}, children: [], element }

      return pipe(
        entities.brands,
        array.findFirst(idEquals(element.id)),
        option.fold(
          () => <></>,
          (brand) => <BrandLinkElement key={key} {...props} brand={brand} />
        )
      )
    }

    default:
      return (
        <p key={key}>
          {pipe(
            element.children,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            array.mapWithIndex((idx, d) => render(idx, d, entities))
          )}
        </p>
      )
  }
}

const whitespaceChar = "\uFEFF"

const renderLeaf = (leaf: CustomText, key: Key) => {
  let children: string | ReactNode = leaf.text.trim() === "" ? whitespaceChar : leaf.text

  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.underline) {
    children = <u>{children}</u>
  }

  return <Fragment key={key}>{children}</Fragment>
}

const render = (key: Key, value: CustomElement | CustomText, entities: StructuredTextEntities) => {
  if (isElement(value)) {
    return renderElement(value, key, entities)
  } else if (isLeaf(value)) {
    return renderLeaf(value, key)
  } else {
    throw new Error(`unknown type: ${JSON.stringify(value, null, 2)}`)
  }
}

interface StructuredTextProps {
  data: unknown
  entities: StructuredTextEntities
}

export const StructuredText: FC<StructuredTextProps> = ({ data, entities }) => {
  return Array.isArray(data) ? (
    <div className="structured-text">
      {pipe(
        data,
        array.mapWithIndex((idx, d) => render(idx, d, entities))
      )}
    </div>
  ) : null
}
